import React, { FC, useEffect, useRef, useState, useCallback } from "react";
import deepClone from "../../../helpers/deepClone";
import {
  useFormCheckboxChange,
  useFormInputChange,
  useFormModelChange,
} from "../../../helpers/form";
import { SecurityCodeInfoDrawer } from "../../ActionsSelectors/Drawer/SecurityCodeInfoDrawer";
import LinkButton from "../../ActionsSelectors/Button/LinkButton/LinkButton";
import CreditCardExpDateInput from "../../ActionsSelectors/Input/CreditCard/CreditCardExpDateInput";
import CreditCardInput from "../../ActionsSelectors/Input/CreditCard/CreditCardInput";
import CreditCardSecurityCodeInput from "../../ActionsSelectors/Input/CreditCard/CreditCardSecurityCodeInput";
import Column from "../../Fundamentals/Grid/Column";
import composeAddress from "./composeAddress";
import {
  ICreditCardFormProps,
  TCreditCardFormData,
} from "./CreditCardForm.interface";
import Checkbox from "../../ActionsSelectors/Checkbox/Checkbox/Checkbox";
import FormCheckWrapper from "../../Atoms/FormLabel/FormCheckWrapper";
import FormLabel from "../../Atoms/FormLabel/FormLabel";
import { Typography } from "../../Fundamentals/Typography/Typography";
import TextInput from "../../ActionsSelectors/Input/TextInput/TextInput";
import { Button } from "../../ActionsSelectors/Button/Button/Button";

export const creditCardFormInitialState: TCreditCardFormData = {
  creditCardData: {
    cardNumber: "",
    expDate: "",
    securityCode: "",
  },
  billingAddressSameAsShipping: false,
  billingAddress: {
    id: "",
    fullName: "",
    surname: "",
    country: "",
    city: "",
    street: "",
    postalCode: "",
  },
  selectedCreditCard: null,
};

const CreditCardForm: FC<ICreditCardFormProps> = ({
  classes,
  data,
  shippingAddress,
  formProps,
  submitText = "CONTINUE TO Order Overview",
  onSubmit,
}) => {
  const [formData, setFormData] = useState<TCreditCardFormData>(() =>
    deepClone(data || creditCardFormInitialState),
  );

  const oldDataRef = useRef<TCreditCardFormData>(null);

  useEffect(() => {
    if (oldDataRef.current !== data) {
      oldDataRef.current = data;
      setFormData(data);
    }
  }, [data]);

  const handleSubmit = (event: React.FormEvent) => {
    /** in case we need uncontrolled form (like, outer API's, payment systems, etc.) */
    if (!onSubmit) return;

    event.preventDefault();
    onSubmit(event, formData);
  };

  const [securityDrawerVisible, setSecurityDrawerVisible] = useState(false);

  const handleSecurityDrawerClose = useCallback(() => {
    setSecurityDrawerVisible(false);
  }, []);

  const handleSecurityDrawerShow = useCallback(() => {
    setSecurityDrawerVisible(true);
  }, []);

  const handleInputChange = useFormInputChange(formData, setFormData);
  const handleCheckboxChange = useFormCheckboxChange(formData, setFormData);
  const handleModelChange = useFormModelChange(formData, setFormData);

  const composedShippingAddress = composeAddress(shippingAddress);

  return (
    <form
      className={classes?.root || "row"}
      onSubmit={handleSubmit}
      {...formProps}
    >
      <Column xs={12} lg={6} classes={{ root: "mb-8 mb-lg-7" }}>
        <CreditCardInput
          label="Card Number"
          name="creditCardData.cardNumber"
          value={formData.creditCardData.cardNumber}
          onModelChange={handleModelChange}
        />
      </Column>

      <Column xs={12} lg={3} classes={{ root: "mb-8 mb-lg-7" }}>
        <CreditCardExpDateInput
          label="Expiration Date"
          name="creditCardData.expDate"
          value={formData.creditCardData.expDate}
          onModelChange={handleModelChange}
        />
      </Column>

      <Column xs={12} lg={3} classes={{ root: "mb-3" }}>
        <CreditCardSecurityCodeInput
          label="Security code"
          name="cardNumber.securityCode"
          value={formData.creditCardData.securityCode}
          onModelChange={handleModelChange}
        />
      </Column>

      <Column xs={12} classes={{ root: "mb-9" }}>
        <LinkButton type="button" onClick={handleSecurityDrawerShow}>
          Where is the security code?
        </LinkButton>
        <SecurityCodeInfoDrawer
          opened={securityDrawerVisible}
          onClose={handleSecurityDrawerClose}
        />
      </Column>
      <Column xs={12} classes={{ root: "mb-8" }}>
        <div className="d-inline-block">
          <FormCheckWrapper>
            <Checkbox
              id="itemlight1"
              checked={formData.billingAddressSameAsShipping}
              name="billingAddressSameAsShipping"
              onChange={handleCheckboxChange}
            />
            <FormLabel
              htmlFor="itemlight1"
              classes={{ root: "form-check-label--grey" }}
            >
              Billing address same as shipping
            </FormLabel>
          </FormCheckWrapper>
        </div>
        {/** in case of null, undefined, etc. */}
        {formData.billingAddressSameAsShipping === true ? (
          <Typography
            variant="text-regular"
            classes={{ root: "text-color-blue mt-6" }}
            as="div"
          >
            {composedShippingAddress}
          </Typography>
        ) : null}
      </Column>

      {formData.billingAddressSameAsShipping === false ? (
        <>
          <Column xs={12} classes={{ root: "mb-8" }}>
            <TextInput
              id="fname"
              label="Name"
              placeholder="Full name"
              autoComplete="given-name"
              name="billingAddress.fullName"
              value={formData.billingAddress.fullName}
              onChange={handleInputChange}
            />
          </Column>

          <Column xs={12} classes={{ root: "mb-8" }}>
            <TextInput
              id="fSurname"
              label="Surname"
              placeholder="Surname"
              autoComplete="family-name"
              name="billingAddress.surname"
              value={formData.billingAddress.surname}
              onChange={handleInputChange}
            />
          </Column>

          <Column xs={12} classes={{ root: "mb-8" }}>
            <TextInput
              id="frmAddressS"
              label="Street and house number"
              placeholder="Street and house number"
              autoComplete="shipping street-address"
              name="billingAddress.street"
              value={formData.billingAddress.street}
              onChange={handleInputChange}
            />
          </Column>

          <Column xs={12} classes={{ root: "mb-8" }}>
            <TextInput
              id="frmCountryS"
              label="Country"
              placeholder="Country"
              autoComplete="shipping country"
              name="billingAddress.country"
              value={formData.billingAddress.country}
              onChange={handleInputChange}
            />
          </Column>

          <Column xs={12} md={6} classes={{ root: "mb-8" }}>
            <TextInput
              id="frmCityS"
              label="City"
              placeholder="City"
              autoComplete="shipping locality"
              name="billingAddress.city"
              value={formData.billingAddress.city}
              onChange={handleInputChange}
            />
          </Column>

          <Column xs={12} md={6} classes={{ root: "mb-8" }}>
            <TextInput
              id="frmZipS"
              label="Postal code"
              placeholder="Postal code"
              autoComplete="shipping postal-code"
              name="billingAddress.postalCode"
              value={formData.billingAddress.postalCode}
              onChange={handleInputChange}
            />
          </Column>
        </>
      ) : null}

      <Button
        variant="conversion"
        classes={{ root: classes?.submitBtn || "mb-3 w-100 w-md-auto" }}
        type="submit"
        data-testid="CreditCardForm__SubmitBtn"
      >
        {submitText}
      </Button>
    </form>
  );
};

export default CreditCardForm;
